import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { customerAuthService } from '../services/customerAuthService';

interface CustomerAuthContextProps {
  customer: any;
  isAuthenticated: boolean;
  login: (cpf: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const CustomerAuthContext = createContext<CustomerAuthContextProps | undefined>(undefined);

export const CustomerAuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('customer_auth'));
  const [customer, setCustomer] = useState<any>(() => {
    const stored = localStorage.getItem('customer_auth');
    return stored ? JSON.parse(stored) : null;
  });

  useEffect(() => {
    const checkAuth = () => {
      const customerAuth = localStorage.getItem('customer_auth');
      setIsAuthenticated(!!customerAuth);
      setCustomer(customerAuth ? JSON.parse(customerAuth) : null);
    };

    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const login = async (cpf: string, password: string) => {
    if (!cpf || !password) {
      return { success: false, error: 'CPF e senha são obrigatórios.' };
    }

    try {
      const response = await customerAuthService.login(cpf, password);
      const { user: customerData, token } = response.data.data;
      
      setIsAuthenticated(true);
      setCustomer(customerData);
      localStorage.setItem('customer_auth', JSON.stringify(customerData));
      localStorage.setItem('customer_token', token);
      
      return { success: true };
    } catch (error: any) {
      let errorMsg = 'Não foi possível fazer login. Verifique suas credenciais.';
      const backendError =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;

      if (backendError === 'Invalid credentials') {
        errorMsg = 'CPF ou senha incorretos.';
      } else if (typeof backendError === 'string' && backendError !== 'Login failed') {
        errorMsg = backendError;
      }
      return { success: false, error: errorMsg };
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setCustomer(null);
    localStorage.removeItem('customer_auth');
    localStorage.removeItem('customer_token');
  };

  return (
    <CustomerAuthContext.Provider value={{ customer, isAuthenticated, login, logout }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
}; 